import { availableAuditEntriesAtom } from '@/global-store';
import { api } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import type { AuditData } from '@eye-able/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

export interface RescanAuditEntriesInput {
  auditEntryIds: number[];
}

export type RescanAuditEntryResponse = Pick<
  AuditData,
  'id' | 'lastAudit' | 'rescanCounter' | 'scanImmediately' | 'errorFlag'
>;

/** Schedules an immediate rescan for every selected report domain. */
export function useRescanAuditEntries() {
  const queryClient = useQueryClient();
  const [, setAvailableAuditEntries] = useAtom(availableAuditEntriesAtom);

  return useMutation({
    mutationFn: async ({ auditEntryIds }: RescanAuditEntriesInput) =>
      Promise.all(
        auditEntryIds.map((entryId) =>
          fetchWrapper.put<RescanAuditEntryResponse>(api.reportEntryRescan, {
            entryId,
          }),
        ),
      ),
    onSuccess: async (updatedEntries) => {
      const updatedEntriesById = new Map(
        updatedEntries.map((entry) => [entry.id, entry]),
      );

      setAvailableAuditEntries((entries: AuditData[]) =>
        entries.map((entry) => ({
          ...entry,
          ...updatedEntriesById.get(entry.id),
        })),
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [api.governanceOverview] }),
        queryClient.invalidateQueries({ queryKey: [api.policies] }),
      ]);

      return updatedEntries;
    },
  });
}
